import { useMutation, useQueryClient } from '@tanstack/react-query'
import { set as _set } from 'radash'

import { getServerLikesActive } from '@wwt/core/admin'
import { Offer } from '@wwt/entities/search-response'

import { getFullRoomId } from '@/entities/offer/index.ts'
import { getSavedOffer } from '@/entities/offer/utils/getSavedOffer.ts'
import { setKeys, useActiveSet } from '@/entities/sets'
import { SetKeys, SetTypes } from '@/entities/sets/config/setKeys.ts'
import { useCreateSet } from '@/entities/sets/hooks/useCreateSet.ts'
import { SetMutationUtils } from '@/entities/sets/utils/setMutationUtils.ts'
import { assert } from '@/shared/lib/assert.ts'

import { MAX_OFFERS_IN_SET } from '../config/consts.ts'
import {
	DislikeKeys,
	DislikeTypes,
	dislikeKeys
} from '../config/dislikeQueryKeys.ts'
import { LikeKeys, LikeTypes, likeKeys } from '../config/likeQueryKeys.ts'
import { likeOfferQuery } from '../query/likeOfferQuery.ts'
import { DislikeMutationUtils } from '../utils/dislikeMutationUtils.ts'
import { LikeMutationUtils } from '../utils/likeMutationUtils.ts'

export const useLikeMutation = () => {
	const queryClient = useQueryClient()
	const activeSet = useActiveSet()
	const createSet = useCreateSet()

	const mutation = useMutation({
		mutationKey: likeKeys.create(),
		mutationFn: async (currentOffer: Offer) => {
			const setId = activeSet?.id

			if (!getServerLikesActive()) {
				return
			}

			const savedOffer = await getSavedOffer(currentOffer)

			return await likeOfferQuery(savedOffer.wwtOfferId, setId ?? '')
		},
		onMutate: async (offer: Offer) => {
			let setId = activeSet?.id

			if (!activeSet || activeSet.totalOffers >= MAX_OFFERS_IN_SET) {
				// DEV: Refactor in future
				const set = await createSet()
				setId = set?.id
			}

			const keys = {
				sets: setKeys.all(),
				likes: likeKeys.all(activeSet?.id),
				ids: likeKeys.ids(),
				dislikes: dislikeKeys.all()
			}

			/* prettier-ignore */
			const previous = {
				sets: queryClient.getQueryData<SetTypes['all'], SetKeys['all']>(keys.sets),
				likes: queryClient.getQueryData<LikeTypes['all'], LikeKeys['all']>(keys.likes),
				ids: queryClient.getQueryData<LikeTypes['ids'], LikeKeys['ids']>(keys.ids),
				dislikes: queryClient.getQueryData<DislikeTypes['all'], DislikeKeys['all']>(keys.dislikes)
			}

			const offerId = offer.IDs.nonPersistingOfferId
			const isLiked = Boolean(previous.ids && previous.ids[offerId])

			assert(
				isLiked === false,
				'If offer liked we should call unlike instead of like mutation'
			)

			await queryClient.cancelQueries({ queryKey: keys.likes })
			await queryClient.cancelQueries({ queryKey: keys.ids })
			await queryClient.cancelQueries({ queryKey: keys.dislikes })

			queryClient.setQueryData<SetTypes['all'], SetKeys['all']>(
				keys.sets,
				saved => SetMutationUtils.addNumberToTotalOffers(saved, setId, 1)
			)

			queryClient.setQueryData<LikeTypes['ids'], LikeKeys['ids']>(
				keys.ids,
				saved => LikeMutationUtils.addOfferToLikeIds(saved, offer, setId)
			)

			const isDisliked = Boolean(
				previous.dislikes &&
					previous.dislikes.disliked.includes(getFullRoomId(offer))
			)

			if (isDisliked) {
				queryClient.setQueryData<DislikeTypes['all'], DislikeKeys['all']>(
					keys.dislikes,
					saved => DislikeMutationUtils.removeOfferFromDisliked(saved, offer)
				)
			}

			/**
			 * Add offer to likes only if server likes is not enabled
			 *
			 * If server likes enabled we invalidate query
			 */
			if (!getServerLikesActive()) {
				/**
				 * Set [] as value of likes if it's undefined on now
				 * We cannot do this on init query client because likes key depends on setId
				 */
				if (previous.likes === undefined) {
					queryClient.setQueryData<LikeTypes['all'], LikeKeys['all']>(
						keys.likes,
						() => []
					)
				}

				queryClient.setQueryData<LikeTypes['all'], LikeKeys['all']>(
					keys.likes,
					saved =>
						LikeMutationUtils.addOfferWithChangeStatusToLikesList(saved, offer)
				)
			}

			return {
				keys,
				previous
			}
		},

		/* prettier-ignore */
		onError: (_err, _liked, context) => {
			assert(context !== undefined, 'onError context not found in like mutation')

			queryClient.setQueryData<SetTypes['all'], SetKeys['all']>(context.keys.sets, context.previous.sets)
			queryClient.setQueryData<LikeTypes['all'], LikeKeys['all']>(context.keys.likes, context.previous.likes)
			queryClient.setQueryData<LikeTypes['ids'], LikeKeys['ids']>(context.keys.ids, context.previous.ids)
			queryClient.setQueryData<DislikeTypes['all'], DislikeKeys['all']>(context.keys.dislikes, context.previous.dislikes)
		},

		/* prettier-ignore */
		onSettled: async (_, _err, _offer, context) => {
			assert(context !== undefined, 'onSettled context not found in like mutation')

			await queryClient.cancelQueries({ queryKey: context.keys.likes })
			await queryClient.cancelQueries({ queryKey: context.keys.ids })
			await queryClient.cancelQueries({ queryKey: context.keys.dislikes })

			await queryClient.invalidateQueries({ queryKey: context.keys.sets })
			await queryClient.invalidateQueries({ queryKey: context.keys.likes })
			await queryClient.invalidateQueries({ queryKey: context.keys.ids })
			await queryClient.invalidateQueries({ queryKey: context.keys.dislikes })
		}
	})

	return mutation
}
